import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, Loader2, X, ExternalLink } from "lucide-react";
import { api } from "../lib/api";

const EMPTY = {
  title: "",
  slug: "",
  author: "Jordy Trachtenberg",
  date: new Date().toISOString().slice(0, 10),
  excerpt: "",
  photo: "",
  body: "",
  status: "draft",
  seo_title: "",
  seo_description: "",
};

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="font-gothic text-xs uppercase tracking-[0.2em] text-white/50">{label}</span>
    {children}
    {hint && <span className="mt-1 block font-body text-xs text-white/35">{hint}</span>}
  </label>
);

const inputCls = "mt-2 w-full border border-white/15 bg-ink px-4 py-3 font-body text-sm text-white outline-none focus:border-flame";

export const ArticlesManager = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | article object
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/articles");
      setArticles(res.data.articles || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditing("new"); setError(""); };
  const openEdit = async (a) => {
    const res = await api.get(`/admin/articles/${a.id}`);
    setForm({ ...EMPTY, ...res.data });
    setEditing(res.data);
    setError("");
  };
  const close = () => { setEditing(null); setForm(EMPTY); setError(""); };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.title.trim() || !form.date.trim()) {
      setError("Title and date are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = { ...form };
      if (editing === "new") {
        await api.post("/admin/articles", payload);
      } else {
        await api.put(`/admin/articles/${editing.id}`, payload);
      }
      close();
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save article.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    await api.delete(`/admin/articles/${a.id}`);
    await load();
  };

  return (
    <div className="mt-16" data-testid="articles-manager">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-light text-white">Tattoo Times</h2>
        <button
          data-testid="article-new"
          onClick={openNew}
          className="inline-flex items-center gap-2 border border-flame bg-flame px-6 py-3 font-body text-xs uppercase tracking-[0.2em] text-ink transition-colors hover:bg-ember"
        >
          <Plus size={15} /> New Article
        </button>
      </div>

      <div className="mt-6 overflow-hidden border border-white/10">
        <table className="w-full text-left" data-testid="articles-table">
          <thead>
            <tr className="border-b border-white/10 bg-surface">
              <th className="px-6 py-4 font-gothic text-xs uppercase tracking-[0.2em] text-white/50">Title</th>
              <th className="px-6 py-4 font-gothic text-xs uppercase tracking-[0.2em] text-white/50">Date</th>
              <th className="px-6 py-4 font-gothic text-xs uppercase tracking-[0.2em] text-white/50">Status</th>
              <th className="px-6 py-4 text-right font-gothic text-xs uppercase tracking-[0.2em] text-white/50">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="px-6 py-12 text-center"><Loader2 className="mx-auto animate-spin text-flame" size={22} /></td></tr>
            ) : articles.length === 0 ? (
              <tr><td colSpan={4} className="px-6 py-12 text-center font-body text-sm text-white/40">No articles yet. Create your first post.</td></tr>
            ) : (
              articles.map((a) => (
                <tr key={a.id} data-testid="article-row" className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-6 py-4 font-body text-sm text-white">{a.title}</td>
                  <td className="px-6 py-4 font-body text-sm text-white/50">{a.date}</td>
                  <td className="px-6 py-4">
                    <span className={`border px-2.5 py-1 font-body text-xs uppercase tracking-wider ${a.status === "published" ? "border-green-500/40 bg-green-500/10 text-green-400" : "border-white/20 bg-white/5 text-white/50"}`}>
                      {a.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-3">
                      {a.status === "published" && (
                        <a href={`/tattoo-times/${a.slug}`} target="_blank" rel="noreferrer" className="text-white/50 hover:text-flame" aria-label="View"><ExternalLink size={16} /></a>
                      )}
                      <button data-testid={`article-edit-${a.slug}`} onClick={() => openEdit(a)} className="text-white/50 hover:text-flame" aria-label="Edit"><Pencil size={16} /></button>
                      <button onClick={() => remove(a)} className="text-white/50 hover:text-red-400" aria-label="Delete"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/80 p-4 md:p-10" data-testid="article-editor">
          <div className="w-full max-w-3xl border border-white/10 bg-surface p-6 md:p-8">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl text-white">{editing === "new" ? "New Article" : "Edit Article"}</h3>
              <button onClick={close} className="text-white/50 hover:text-white" aria-label="Close"><X size={20} /></button>
            </div>

            {error && <p className="mt-4 border border-red-500/30 bg-red-500/10 px-4 py-2 font-body text-sm text-red-400">{error}</p>}

            <div className="mt-6 grid gap-5">
              <Field label="Title"><input data-testid="article-field-title" className={inputCls} value={form.title} onChange={set("title")} /></Field>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Publication Date"><input type="date" className={inputCls} value={form.date} onChange={set("date")} /></Field>
                <Field label="Author"><input className={inputCls} value={form.author} onChange={set("author")} /></Field>
              </div>
              <Field label="Slug (URL)" hint={`Leave blank to auto-generate. URL: /tattoo-times/${form.slug || "your-title"}`}>
                <input className={inputCls} value={form.slug} onChange={set("slug")} placeholder="auto from title" />
              </Field>
              <Field label="Photo URL (optional)" hint="Paste a hosted image URL. Leave blank for no photo.">
                <input className={inputCls} value={form.photo || ""} onChange={set("photo")} placeholder="https://…" />
              </Field>
              <Field label="Excerpt" hint="Short summary shown on the blog list.">
                <textarea className={`${inputCls} min-h-[70px]`} value={form.excerpt} onChange={set("excerpt")} />
              </Field>
              <Field label="Body" hint='Separate paragraphs with a blank line. Start a line with "## " for a section heading.'>
                <textarea data-testid="article-field-body" className={`${inputCls} min-h-[260px] font-mono`} value={form.body} onChange={set("body")} />
              </Field>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="SEO Title (optional)"><input className={inputCls} value={form.seo_title || ""} onChange={set("seo_title")} /></Field>
                <Field label="Status">
                  <select data-testid="article-field-status" className={inputCls} value={form.status} onChange={set("status")}>
                    <option value="draft">Draft (hidden)</option>
                    <option value="published">Published</option>
                  </select>
                </Field>
              </div>
              <Field label="SEO Meta Description (optional)">
                <textarea className={`${inputCls} min-h-[60px]`} value={form.seo_description || ""} onChange={set("seo_description")} />
              </Field>
            </div>

            <div className="mt-8 flex items-center justify-end gap-3">
              <button onClick={close} className="border border-white/15 px-6 py-3 font-body text-xs uppercase tracking-[0.2em] text-white/70 hover:border-white/40">Cancel</button>
              <button
                data-testid="article-save"
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-2 border border-flame bg-flame px-8 py-3 font-body text-xs uppercase tracking-[0.2em] text-ink transition-colors hover:bg-ember disabled:opacity-50"
              >
                {saving && <Loader2 className="animate-spin" size={15} />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
